/**
 * V2 source path: customer/midas/components/v2/MidasV2Layout.tsx
 * V1 source: none (extracted from MidasPageV2.tsx in Phase 4.1)
 * Strategy: scratch (extracts the V2 sidebar shell so it renders on both
 *           /midas-v2 and /midas-v2/history per spec D1)
 * Verified no-MUI: yes
 * Phase: 4.1
 *
 * Shared route-layout for the MIDAS V2 sub-tree. Renders the V2 sidebar shell
 * (260/280px on desktop, 56px rail + tablet drawer, phone full-screen takeover)
 * and an <Outlet /> for the active sub-page (chat at /midas-v2 or history at
 * /midas-v2/history).
 *
 * Spec ref:
 *   IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/phase 4/PHASE4_HISTORY_FIX_SPEC.md
 *   §3A (shell composition), §4.1 (sub-phase 4.1 acceptance), §6 D1 (top bar
 *   stays as-is, sidebar-only fix).
 *
 * Deliberate non-changes (Phase 4.1):
 *   - The platform's existing global MachineID app top bar (rendered above
 *     this layout by the parent customer route layer) is NOT touched.
 *   - The chat-page top bar with "+ Ask Midas" lives inside MidasPageV2.tsx
 *     and renders for the chat sub-page only. No top-bar component is built
 *     or moved here.
 *
 * Outlet context shape (consumed via `useOutletContext<MidasV2OutletContext>()`):
 *   - openPhoneSidebar: () => void  // chat page hamburger calls this
 *
 * Sidebar tab default:
 *   The Fleet/History picker inside MidasSidebarV2 reads `initialTab`. We
 *   derive it from the URL — /midas-v2/history → "history", everything else
 *   under /midas-v2 → "fleet" (or whatever the rail tile last selected). The
 *   derived value is re-keyed on the sidebar instance via the `key` prop so
 *   the picker resets when the user navigates between the two routes.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import { TooltipProvider } from "./ui/tooltip";
import MidasDebugHarnessV2 from "./MidasDebugHarnessV2";
import MidasSidebarRailV2 from "./MidasSidebarRailV2";
import MidasSidebarV2 from "./MidasSidebarV2";
import { useMidasBreakpoint } from "./hooks/useMidasBreakpoint";
import { FleetHistoryTab } from "./MidasFleetHistoryPickerV2";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  fetchHistoryThreads,
} from "../../midasHistoryPageSlice";

// ════════════════════════════════════════════════════════════════
// Constants — match Paper Phase 3 — Sidebar — Spec — Tablet
// ════════════════════════════════════════════════════════════════

const SIDEBAR_EXPANDED_WIDTH = 280; // desktop expanded
const SIDEBAR_RAIL_WIDTH = 56; // tablet default + desktop collapsed
const TABLET_DRAWER_WIDTH = 320; // tablet expanded overlay drawer
const SCRIM_OPACITY = 0.32;

// ════════════════════════════════════════════════════════════════
// Outlet context — what this layout exposes to its child routes
// ════════════════════════════════════════════════════════════════

export interface MidasV2OutletContext {
  /** Open the phone sidebar takeover. Wired to the chat page's TopBar
   *  hamburger. No-op on tablet/desktop (sidebar is always visible there). */
  openPhoneSidebar: () => void;
}

// ════════════════════════════════════════════════════════════════
// usePrefersReducedMotion (copied verbatim from MidasPageV2)
// ════════════════════════════════════════════════════════════════

const usePrefersReducedMotion = (): boolean => {
  const [pref, setPref] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setPref(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    if (typeof (mql as any).addListener === "function") {
      (mql as any).addListener(handler);
      return () => (mql as any).removeListener(handler);
    }
    return undefined;
  }, []);
  return pref;
};

// ════════════════════════════════════════════════════════════════
// Route → initial tab mapping
// ════════════════════════════════════════════════════════════════

const initialTabForPath = (pathname: string): FleetHistoryTab => {
  // Treat anything that ends in /history (with or without trailing slash) as
  // the history sub-route. The /midas-v2 chat root keeps the default fleet tab.
  if (/\/midas-v2\/history\/?$/.test(pathname)) return "history";
  return "fleet";
};

// ════════════════════════════════════════════════════════════════
// MidasV2Layout
// ════════════════════════════════════════════════════════════════

const MidasV2Layout: React.FC = () => {
  const { translate } = useTranslation("chatBot");
  const t = (k: string, f: string) =>
    translate(k as any, { defaultValue: f }) as string;
  const { isDesktop, isTablet, isPhone } = useMidasBreakpoint();
  const prefersReducedMotion = usePrefersReducedMotion();
  const location = useLocation();
  const dispatch = useAppDispatch();
  // Sub-phase 4.6 — selector for sidebar count probe (effect below).
  // Sub-phase 4.6-fix (2026-05-04): probe now reads `globalTotal` instead
  // of `total`. `total` reflects the active chip filter on the history
  // page; `globalTotal` only updates on unfiltered fetches and survives
  // `resetHistoryPage` — so the layout probe fires once on a fresh
  // session and stays satisfied across filter changes.
  const historyGlobalTotal = useAppSelector(
    (s) => s.midasHistoryPage?.globalTotal ?? 0
  );

  // Sidebar UI state — kept local because Phase 0 didn't model this in the
  // slice. Desktop: starts expanded. Tablet/phone: starts collapsed (rail/closed).
  const [desktopExpanded, setDesktopExpanded] = useState(true);
  const [tabletDrawerOpen, setTabletDrawerOpen] = useState(false);
  const [phoneSidebarOpen, setPhoneSidebarOpen] = useState(false);
  const [railSelectedTab, setRailSelectedTab] = useState<FleetHistoryTab>("fleet");

  // Route-derived initial tab. Re-evaluates on navigation between
  // /midas-v2 and /midas-v2/history without remounting the rest of the page.
  const routeInitialTab = useMemo(
    () => initialTabForPath(location.pathname),
    [location.pathname]
  );

  // Effective initial tab handed to MidasSidebarV2 — the route wins (so
  // landing directly on /midas-v2/history shows History as active), but the
  // rail's last manual pick is honored for chat-page sessions.
  const initialTab: FleetHistoryTab =
    routeInitialTab === "history" ? "history" : railSelectedTab;

  // The sidebar's internal `activeTab` is held in component state (see
  // MidasSidebarV2.tsx:85). Remount it whenever the routeInitialTab flips so
  // the picker re-syncs to History when the user navigates to /history.
  const sidebarKey = `midas-sidebar-v2-${routeInitialTab}`;

  // Sub-phase 4.6 — sidebar count probe.
  // The Sidebar V2 History tab reads its count from
  // `state.midasHistoryPage.globalTotal` (sub-phase 4.6-fix: was `total`).
  // Previously it was only populated when the user opened
  // `/midas-v2/history`, so the History tab on a fresh session showed `0`.
  // The history page no longer wipes `globalTotal` on unmount — only `total`
  // — so this probe runs once per session for fresh logins and rarely
  // re-fires after that.
  //
  // Strategy: re-run the probe whenever (a) we are NOT currently on the
  // history sub-route (the page itself fetches + replaces) and (b)
  // `globalTotal` is still 0. This covers fresh-session mount without
  // double-firing on the history page (where its own `useEffect` already
  // fetches with the user's active filter — and that fetch will populate
  // `globalTotal` if the active filter is "all").
  useEffect(() => {
    if (historyGlobalTotal !== 0) return;
    if (/\/midas-v2\/history\/?$/.test(location.pathname)) return;
    dispatch(fetchHistoryThreads({ machineFilter: "all" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyGlobalTotal, location.pathname]);

  // Reset overlay states when crossing breakpoints
  useEffect(() => {
    if (!isTablet) setTabletDrawerOpen(false);
    if (!isPhone) setPhoneSidebarOpen(false);
  }, [isTablet, isPhone]);

  // ESC key dismisses overlays
  useEffect(() => {
    if (!tabletDrawerOpen && !phoneSidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setTabletDrawerOpen(false);
        setPhoneSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tabletDrawerOpen, phoneSidebarOpen]);

  // Lock body scroll when phone sidebar is open
  useEffect(() => {
    if (!phoneSidebarOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [phoneSidebarOpen]);

  const handleRailExpand = useCallback(() => {
    if (isTablet) {
      setTabletDrawerOpen(true);
    } else if (isDesktop) {
      setDesktopExpanded(true);
    }
  }, [isDesktop, isTablet]);

  const handleRailSelectTab = useCallback((tab: FleetHistoryTab) => {
    setRailSelectedTab(tab);
  }, []);

  const handleSidebarCollapse = useCallback(() => {
    if (isTablet) {
      setTabletDrawerOpen(false);
    } else if (isDesktop) {
      setDesktopExpanded(false);
    }
  }, [isDesktop, isTablet]);

  // Outlet context — exposes phone-sidebar opener to child pages
  const outletContext: MidasV2OutletContext = useMemo(
    () => ({
      openPhoneSidebar: () => setPhoneSidebarOpen(true),
    }),
    []
  );

  // Animation timing — TRANSITION = 240ms cubic-bezier(0.4,0,0.2,1) per spec
  const drawerTransition = prefersReducedMotion
    ? "none"
    : "transform 240ms cubic-bezier(0.4,0,0.2,1)";

  return (
    <TooltipProvider delayDuration={200}>
      <MidasDebugHarnessV2 />
      <div className="midas-v2-root flex h-full min-h-screen w-full flex-col overflow-hidden bg-midas-bone">
        {/* Body: sidebar + page content (rendered via Outlet) */}
        <main className="relative flex flex-1 min-h-0">
          {/* ── Desktop sidebar (expanded or collapsed rail) ── */}
          {isDesktop && (
            <div
              className="flex-shrink-0"
              style={{
                width: desktopExpanded
                  ? `${SIDEBAR_EXPANDED_WIDTH}px`
                  : `${SIDEBAR_RAIL_WIDTH}px`,
                transition: prefersReducedMotion
                  ? "none"
                  : "width 240ms cubic-bezier(0.4,0,0.2,1)",
              }}
            >
              {desktopExpanded ? (
                <MidasSidebarV2
                  key={sidebarKey}
                  onCollapse={handleSidebarCollapse}
                  initialTab={initialTab}
                  prefersReducedMotion={prefersReducedMotion}
                  variant="desktop"
                  showKeyboardHint={true}
                />
              ) : (
                <MidasSidebarRailV2
                  onExpand={handleRailExpand}
                  onSelectTab={handleRailSelectTab}
                />
              )}
            </div>
          )}

          {/* ── Tablet rail (always visible — overlay drawer floats over) ── */}
          {isTablet && (
            <div className="flex-shrink-0" style={{ width: `${SIDEBAR_RAIL_WIDTH}px` }}>
              <MidasSidebarRailV2
                onExpand={handleRailExpand}
                onSelectTab={handleRailSelectTab}
              />
            </div>
          )}

          {/* ── Page content (chat or history) — flexes to fill remaining width ── */}
          <div className="flex min-w-0 flex-1 flex-col">
            <Outlet context={outletContext} />
          </div>

          {/* ── Tablet overlay drawer + scrim ── */}
          {isTablet && (
            <>
              {/* Backdrop scrim */}
              <div
                aria-hidden={!tabletDrawerOpen}
                onClick={() => setTabletDrawerOpen(false)}
                className="absolute inset-0 z-40"
                style={{
                  backgroundColor: `rgba(42,40,36,${SCRIM_OPACITY})`,
                  opacity: tabletDrawerOpen ? 1 : 0,
                  pointerEvents: tabletDrawerOpen ? "auto" : "none",
                  transition: prefersReducedMotion
                    ? "none"
                    : "opacity 240ms cubic-bezier(0.4,0,0.2,1)",
                  // Don't cover the rail — drawer slides over chat+dock only
                  left: `${SIDEBAR_RAIL_WIDTH}px`,
                }}
              />
              {/* Drawer — slides from left, sits above scrim */}
              <div
                className="absolute top-0 bottom-0 z-50 shadow-[8px_0_32px_rgba(42,40,36,0.18)]"
                style={{
                  left: `${SIDEBAR_RAIL_WIDTH}px`,
                  width: `${TABLET_DRAWER_WIDTH}px`,
                  transform: tabletDrawerOpen
                    ? "translateX(0)"
                    : `translateX(-${TABLET_DRAWER_WIDTH + SIDEBAR_RAIL_WIDTH}px)`,
                  transition: drawerTransition,
                  pointerEvents: tabletDrawerOpen ? "auto" : "none",
                }}
              >
                <MidasSidebarV2
                  key={`${sidebarKey}-drawer`}
                  onCollapse={() => setTabletDrawerOpen(false)}
                  initialTab={initialTab}
                  onAfterItemClick={() => setTabletDrawerOpen(false)}
                  prefersReducedMotion={prefersReducedMotion}
                  variant="drawer"
                  showKeyboardHint={false}
                />
              </div>
            </>
          )}

          {/* ── Phone full-screen sidebar takeover ── */}
          {isPhone && phoneSidebarOpen && (
            <div
              className={`absolute inset-0 z-50 flex flex-col ${
                prefersReducedMotion ? "" : "animate-in fade-in-0 duration-200"
              }`}
              style={{
                paddingTop: "env(safe-area-inset-top)",
                paddingBottom: "env(safe-area-inset-bottom)",
                backgroundColor: "#FBF6EC",
              }}
              role="dialog"
              aria-modal="true"
              aria-label="MIDAS sidebar"
            >
              {/* Phone takeover top bar (sidebar takeover only — NOT the page top bar) */}
              <div className="flex h-12 flex-shrink-0 items-center justify-between border-b border-[rgba(44,38,32,0.08)] px-3">
                <button
                  type="button"
                  onClick={() => setPhoneSidebarOpen(false)}
                  aria-label={t("midas.v2.backToChat", "Back to chat")}
                  className="flex h-11 items-center gap-1.5 rounded-md px-2 font-sora text-[13px] font-medium text-midas-warm-graphite outline-none transition-colors duration-150 hover:bg-[rgba(44,38,32,0.06)] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  <span aria-hidden="true">‹</span>
                  {t("midas.v2.backToChat", "Back to chat")}
                </button>
                <span
                  className="font-sora text-[15px] font-semibold text-midas-graphite"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {t("midas.v2.sidebar", "Sidebar")}
                </span>
                <span aria-hidden="true" className="h-11 w-11" />
              </div>
              {/* Sidebar body — same MidasSidebarV2 in drawer variant */}
              <div className="flex min-h-0 flex-1 flex-col">
                <MidasSidebarV2
                  key={`${sidebarKey}-phone`}
                  initialTab={initialTab}
                  onAfterItemClick={() => setPhoneSidebarOpen(false)}
                  prefersReducedMotion={prefersReducedMotion}
                  variant="drawer"
                  showKeyboardHint={false}
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </TooltipProvider>
  );
};

export default MidasV2Layout;
